import { ReactNode } from "react";
import { RefreshCw } from "lucide-react";
import { motion } from "framer-motion";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { cn } from "@/lib/utils";

interface MobileDashboardWrapperProps {
  children: ReactNode;
  onRefresh: () => Promise<void>;
  className?: string;
}

export function MobileDashboardWrapper({
  children,
  onRefresh,
  className
}: MobileDashboardWrapperProps) {
  // Threshold de 80px para sensação de "peso" e qualidade ao puxar
  const {
    pullDistance,
    isRefreshing,
    isPulling,
    containerRef,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = usePullToRefresh({ onRefresh, threshold: 80 });

  // Cálculo progressivo para animações baseadas na distância do puxão
  const progress = Math.min(pullDistance / 80, 1);

  return (
    <div className="relative h-full bg-muted/30">
      {/* 
        INDICADOR DE REFRESH COM GLASSMORPHISM
        - Esfera de vidro fosco que aparece no topo
        - Escala e rotação baseadas no progresso do puxão
      */}
      <motion.div
        className="absolute left-0 right-0 flex justify-center z-20 pointer-events-none"
        style={{ top: 16 }}
        animate={{
          y: isRefreshing ? 40 : Math.max(0, pullDistance - 30),
          opacity: isPulling || isRefreshing ? 1 : 0,
          scale: isRefreshing ? 1 : 0.8 + (progress * 0.2),
        }}
        transition={{ 
          type: "spring", 
          stiffness: 400, 
          damping: 30 
        }}
      >
        <div 
          className={cn(
            "w-12 h-12 rounded-full flex items-center justify-center",
            "bg-white/80 dark:bg-black/40 backdrop-blur-xl",
            "border border-white/20 dark:border-white/10",
            "shadow-glass",
            isRefreshing && "shadow-primary/20"
          )}
        >
          <motion.div
            animate={{ 
              rotate: isRefreshing ? 360 : progress * 180,
            }}
            transition={isRefreshing ? { 
              repeat: Infinity, 
              duration: 0.8, 
              ease: "linear" 
            } : { 
              type: "spring",
              stiffness: 200
            }}
          >
            <RefreshCw 
              className={cn(
                "h-5 w-5 transition-colors",
                isRefreshing ? "text-primary" : "text-muted-foreground"
              )} 
            />
          </motion.div>
        </div>
      </motion.div>

      {/* 
        CONTEÚDO DA PÁGINA COM EFEITO iOS
        - Scale: O conteúdo encolhe levemente ao puxar (efeito de profundidade)
        - Border Radius: As bordas superiores arredondam ao puxar
      */}
      <motion.div
        ref={containerRef}
        className={cn(
          "h-full overflow-y-auto overflow-x-hidden touch-pan-y safe-area-bottom bg-background",
          className
        )}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        animate={{
          y: isRefreshing ? 80 : 0,
          scale: isRefreshing || (isPulling && pullDistance > 20) ? 0.96 : 1,
          borderTopLeftRadius: isPulling ? "1.5rem" : "0px",
          borderTopRightRadius: isPulling ? "1.5rem" : "0px",
        }}
        transition={{ 
          type: "spring", 
          stiffness: 350, 
          damping: 30,
          mass: 0.8
        }}
        style={{
          transformOrigin: "top center"
        }}
      >
        {children}
      </motion.div>
    </div>
  );
}
